import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProfileForm } from "@/components/admin/ProfileForm";
import { ExperienceManager } from "@/components/admin/ExperienceManager";
import { SkillsManager } from "@/components/admin/SkillsManager";
import { CompetenciesManager } from "@/components/admin/CompetenciesManager";
import { ProjectsManager } from "@/components/admin/ProjectsManager";
import { EducationManager } from "@/components/admin/EducationManager";
import { PreviewFrame } from "@/components/admin/PreviewFrame";
import { LinkedInPostManager } from "@/components/admin/LinkedInPostManager";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { OttoConversationsManager } from "@/components/admin/OttoConversationsManager";
import { SeoManager } from "@/components/admin/SeoManager";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { toast } from "sonner";
import OttoChatBubble from "@/components/OttoChatBubble";

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [showEmbeddedChat, setShowEmbeddedChat] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/admin/login");
      return;
    }

    // Verify admin role
    const { data: isAdmin, error } = await supabase
      .rpc('has_role', { _user_id: session.user.id, _role: 'admin' });

    if (error || !isAdmin) {
      toast.error("Access denied. Admin privileges required.");
      await supabase.auth.signOut();
      navigate("/admin/login");
      return;
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-foreground">Loading...</p>
      </div>
    );
  }

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowEmbeddedChat(true);
  };

  const handleStartNewConversation = () => {
    setSelectedConversationId(null);
    setShowEmbeddedChat(true);
  };

  const handleBackToList = () => {
    setShowEmbeddedChat(false);
    setSelectedConversationId(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "analytics":
        return <AnalyticsDashboard />;
      case "seo":
        return <SeoManager />;
      case "profile":
        return <ProfileForm />;
      case "competencies":
        return <CompetenciesManager />;
      case "experience":
        return <ExperienceManager />;
      case "skills":
        return <SkillsManager />;
      case "projects":
        return <ProjectsManager />;
      case "education":
        return <EducationManager />;
      case "linkedin":
        return <LinkedInPostManager />;
      case "otto-history":
        if (showEmbeddedChat) {
          return (
            <div className="h-[calc(100vh-180px)]">
              <div className="mb-4">
                <button
                  onClick={handleBackToList}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  ← Back to conversations
                </button>
              </div>
              <OttoChatBubble
                conversationId={selectedConversationId}
                onConversationCreated={setSelectedConversationId}
                isEmbedded={true}
              />
            </div>
          );
        }
        return (
          <OttoConversationsManager
            onSelectConversation={handleSelectConversation}
            onStartNewConversation={handleStartNewConversation}
          />
        );
      default:
        return <ProfileForm />;
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case "linkedin":
        return "LinkedIn Posts";
      case "otto-history":
        return showEmbeddedChat ? "Chat with Otto" : "Otto Conversations";
      default:
        return activeTab.replace("-", " ");
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar activeTab={activeTab} onTabChange={(tab) => {
          setActiveTab(tab);
          if (tab !== "otto-history") {
            setShowEmbeddedChat(false);
            setSelectedConversationId(null);
          }
        }} />

        <main className="flex-1 flex flex-col min-h-screen">
          <header className="h-14 border-b border-border flex items-center px-4 bg-card/50 backdrop-blur-sm sticky top-0 z-10">
            <SidebarTrigger className="mr-4" />
            <h2 className="text-base font-semibold text-foreground capitalize tracking-tight">
              {getTabTitle()}
            </h2>
          </header>

          <div className="flex-1 p-6 overflow-auto">
            <div className="max-w-5xl mx-auto">
              {renderContent()}
            </div>
          </div>
        </main>

        {activeTab !== "otto-history" && <OttoChatBubble />}
      </div>
    </SidebarProvider>
  );
};

export default Admin;
